import { useState } from 'react'
import { Dialog, DialogPanel, DialogBackdrop, Textarea } from '@headlessui/react'
import Close from '../../assets/close.svg?react'
import { BlackActionButton } from '../buttons/BlackActionButton'
import { useCreateReviewReply } from '../../queries/useReviews'

interface WriteReplyDialogProps {
  isOpen: boolean
  onClose: () => void
  reviewId: string | null
  reviewUsername?: string
}

export const WriteReplyDialog = ({
  isOpen,
  onClose,
  reviewId,
  reviewUsername,
}: WriteReplyDialogProps) => {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const createReplyMutation = useCreateReviewReply(reviewId ?? '')

  const handleSubmit = () => {
    if (text.trim() === '') {
      setError('Please write a reply')
      return
    }
    if (!reviewId) return

    setError(null)

    createReplyMutation.mutate(text.trim(), {
      onSuccess: () => {
        setIsSuccess(true)
      },
      onError: (error) => {
        console.error('Error submitting reply:', error)
        setError('Failed to submit reply. Please try again.')
      },
    })
  }

  const handleClose = () => {
    setText('')
    setError(null)
    setIsSuccess(false)
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute cursor-pointer top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Close width={16} height={16} />
          </button>

          {isSuccess ? (
            <>
              {/* Success State */}
              <h2 className="text-xl font-semibold mb-2 mt-6">Reply Submitted</h2>
              <p className="text-gray-500 text-sm mb-6">
                Thank you for sharing your thoughts! Your reply has been published.
              </p>
              <div className="flex justify-end">
                <BlackActionButton
                  label="Done"
                  onClick={handleClose}
                />
              </div>
            </>
          ) : (
            <>
              {/* Title */}
              <h2 className="text-xl font-semibold mb-2 mt-6">
                {reviewUsername ? `Reply to ${reviewUsername}` : 'Write a reply'}
              </h2>

              {/* Description */}
              <p className="text-gray-500 text-sm mb-6">
                Share your thoughts on this review. Be respectful and constructive.
              </p>

              {/* Text Area */}
              <Textarea
                value={text}
                maxLength={400}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write your reply..."
                className="w-full h-32 p-3 mb-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              />

              {/* Error Message */}
              {error && (
                <p className="text-sm text-red-600 mb-4">{error}</p>
              )}

              {/* Submit Button */}
              <div className="flex justify-end">
                <BlackActionButton
                  label={createReplyMutation.isPending ? 'Submitting...' : 'Submit Reply'}
                  onClick={handleSubmit}
                />
              </div>
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
